export interface ThemeColors {
  primary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  accent: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
}

export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
  backgroundImage?: string;
  headerStyle: 'solid' | 'gradient' | 'image';
  whimsyLevel: 'minimal' | 'moderate' | 'high';
  customCSS?: string;
}

export const defaultThemes: Theme[] = [
  {
    id: 'professional',
    name: 'Professional Blue',
    description: 'Clean, professional design with subtle blue accents',
    colors: {
      primary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
      },
      accent: {
        50: '#f0fdfa',
        100: '#ccfbf1',
        200: '#99f6e4',
        300: '#5eead4',
        400: '#2dd4bf',
        500: '#14b8a6',
        600: '#0d9488',
        700: '#0f766e',
        800: '#115e59',
        900: '#134e4a',
      }
    },
    headerStyle: 'gradient',
    whimsyLevel: 'minimal'
  },
  {
    id: 'ocean-breeze',
    name: 'Ocean Breeze',
    description: 'Soft ocean-inspired colors with excellent readability',
    colors: {
      primary: {
        50: '#f0f9ff',
        100: '#e0f2fe',
        200: '#bae6fd',
        300: '#7dd3fc',
        400: '#38bdf8',
        500: '#0ea5e9',
        600: '#0284c7',
        700: '#0369a1',
        800: '#075985',
        900: '#0c4a6e',
      },
      accent: {
        50: '#f0fdfa',
        100: '#ccfbf1',
        200: '#99f6e4',
        300: '#5eead4',
        400: '#2dd4bf',
        500: '#14b8a6',
        600: '#0d9488',
        700: '#0f766e',
        800: '#115e59',
        900: '#134e4a',
      }
    },
    headerStyle: 'gradient',
    whimsyLevel: 'moderate'
  },
  {
    id: 'sunset-palette',
    name: 'Sunset Palette',
    description: 'Warm sunset colors with coral and peach accents',
    colors: {
      primary: {
        50: '#fdf2f8',
        100: '#fce7f3',
        200: '#fbcfe8',
        300: '#f9a8d4',
        400: '#f472b6',
        500: '#ec4899',
        600: '#db2777',
        700: '#be185d',
        800: '#9d174d',
        900: '#831843',
      },
      accent: {
        50: '#fff7ed',
        100: '#ffedd5',
        200: '#fed7aa',
        300: '#fdba74',
        400: '#fb923c',
        500: '#f97316',
        600: '#ea580c',
        700: '#c2410c',
        800: '#9a3412',
        900: '#7c2d12',
      }
    },
    headerStyle: 'gradient',
    whimsyLevel: 'moderate'
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    description: 'Natural forest greens with earthy tones',
    colors: {
      primary: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d',
      },
      accent: {
        50: '#fefdf8',
        100: '#fef9e7',
        200: '#fef0c7',
        300: '#fde68a',
        400: '#fcd34d',
        500: '#f59e0b',
        600: '#d97706',
        700: '#b45309',
        800: '#92400e',
        900: '#78350f',
      }
    },
    headerStyle: 'gradient',
    whimsyLevel: 'moderate'
  }
];

class ThemeService {
  private static instance: ThemeService;
  private currentTheme: Theme;
  private customThemes: Theme[] = [];
  private listeners: ((theme: Theme) => void)[] = [];

  static getInstance(): ThemeService {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  constructor() {
    this.currentTheme = this.loadThemeFromStorage();
    this.applyTheme(this.currentTheme);
  }

  private loadThemeFromStorage(): Theme {
    try {
      const savedThemeId = localStorage.getItem('salty_mermaid_theme');
      if (savedThemeId) {
        const theme = [...defaultThemes, ...this.customThemes].find(t => t.id === savedThemeId);
        if (theme) return theme;
      }
    } catch (error) {
      console.warn('Failed to load theme from storage:', error);
    }
    return defaultThemes[0]; // Default to professional theme
  }

  private saveThemeToStorage(theme: Theme): void {
    try {
      localStorage.setItem('salty_mermaid_theme', theme.id);
      if (theme.id.startsWith('custom_')) {
        const customThemes = this.customThemes.filter(t => t.id !== theme.id);
        customThemes.push(theme);
        localStorage.setItem('salty_mermaid_custom_themes', JSON.stringify(customThemes));
        this.customThemes = customThemes;
      }
    } catch (error) {
      console.warn('Failed to save theme to storage:', error);
    }
  }

  private applyTheme(theme: Theme): void {
    // Apply CSS custom properties
    const root = document.documentElement;
    
    // Apply primary colors
    Object.entries(theme.colors.primary).forEach(([key, value]) => {
      root.style.setProperty(`--color-primary-${key}`, value);
    });

    // Apply accent colors
    Object.entries(theme.colors.accent).forEach(([key, value]) => {
      root.style.setProperty(`--color-accent-${key}`, value);
    });

    // Apply background image if provided
    if (theme.backgroundImage) {
      root.style.setProperty('--theme-background-image', `url(${theme.backgroundImage})`);
    } else {
      root.style.removeProperty('--theme-background-image');
    }

    // Apply custom CSS if provided
    this.updateCustomCSS(theme.customCSS);

    this.currentTheme = theme;
  }

  private updateCustomCSS(customCSS?: string): void {
    // Remove existing custom theme CSS
    const existingStyle = document.getElementById('custom-theme-css');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add new custom CSS if provided
    if (customCSS) {
      const style = document.createElement('style');
      style.id = 'custom-theme-css';
      style.textContent = customCSS;
      document.head.appendChild(style);
    }
  }

  getCurrentTheme(): Theme {
    return this.currentTheme;
  }

  getAllThemes(): Theme[] {
    return [...defaultThemes, ...this.customThemes];
  }

  setTheme(themeId: string): boolean {
    const theme = [...defaultThemes, ...this.customThemes].find(t => t.id === themeId);
    if (!theme) return false;

    this.applyTheme(theme);
    this.saveThemeToStorage(theme);
    this.notifyListeners();
    return true;
  }

  createCustomTheme(theme: Omit<Theme, 'id'>): Theme {
    const customTheme: Theme = {
      ...theme,
      id: `custom_${Date.now()}`
    };

    this.customThemes.push(customTheme);
    this.saveThemeToStorage(customTheme);
    return customTheme;
  }

  updateCustomTheme(themeId: string, updates: Partial<Theme>): boolean {
    const themeIndex = this.customThemes.findIndex(t => t.id === themeId);
    if (themeIndex === -1) return false;

    this.customThemes[themeIndex] = { ...this.customThemes[themeIndex], ...updates };
    
    if (this.currentTheme.id === themeId) {
      this.applyTheme(this.customThemes[themeIndex]);
    }

    this.saveThemeToStorage(this.customThemes[themeIndex]);
    this.notifyListeners();
    return true;
  }

  deleteCustomTheme(themeId: string): boolean {
    if (!themeId.startsWith('custom_')) return false;

    this.customThemes = this.customThemes.filter(t => t.id !== themeId);
    
    if (this.currentTheme.id === themeId) {
      this.setTheme(defaultThemes[0].id);
    }

    try {
      localStorage.setItem('salty_mermaid_custom_themes', JSON.stringify(this.customThemes));
    } catch (error) {
      console.warn('Failed to save updated custom themes:', error);
    }

    this.notifyListeners();
    return true;
  }

  subscribe(listener: (theme: Theme) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.currentTheme));
  }

  // Generate theme from uploaded image (placeholder for future AI integration)
  async generateThemeFromImage(imageFile: File): Promise<Theme | null> {
    try {
      // This is a placeholder - in a real implementation, you'd:
      // 1. Upload the image to a service
      // 2. Use AI/ML to extract dominant colors
      // 3. Generate a cohesive color palette
      
      // For now, we'll create a sample theme
      const fileName = imageFile.name.toLowerCase();
      let baseTheme: Theme;
      
      if (fileName.includes('ocean') || fileName.includes('blue')) {
        baseTheme = defaultThemes[1]; // Ocean Breeze
      } else if (fileName.includes('sunset') || fileName.includes('warm')) {
        baseTheme = defaultThemes[2]; // Sunset Palette
      } else if (fileName.includes('forest') || fileName.includes('green')) {
        baseTheme = defaultThemes[3]; // Forest Green
      } else {
        baseTheme = defaultThemes[0]; // Professional
      }

      const customTheme: Omit<Theme, 'id'> = {
        name: `Custom Theme - ${imageFile.name}`,
        description: `Auto-generated theme based on ${imageFile.name}`,
        colors: baseTheme.colors,
        backgroundImage: URL.createObjectURL(imageFile),
        headerStyle: 'image',
        whimsyLevel: 'moderate'
      };

      return this.createCustomTheme(customTheme);
    } catch (error) {
      console.error('Failed to generate theme from image:', error);
      return null;
    }
  }
}

export const themeService = ThemeService.getInstance();